import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AuthenticationService, PageService, RouterDataService, SePageComponent } from '@se/platform';
import { Logger } from '@se/se.platform.base';
import { BehaviorSubject, combineLatest, filter, map, Subscription } from 'rxjs';
import { Instance, Series } from 'larvitar/dist/imaging/types';
import { isNil, keys } from 'lodash-es';
import { PatientDetails } from '../../models/dto/patient-details';
import { Patients } from '../../models/internal/Patients';
import { SeriesElement } from '../../models/internal/SeriesElement';
import { mergeStudyArrays } from '../../models/view/merger';
import { OriginPage } from '../../../../models';
import { AsyncPipe, DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { ViewStudy } from '../../models/view/view-study';
import { DicomUploadService, RawRequest } from '../../services/dicom-upload/dicom-upload.service';
import { StowrsInputElement } from '../../services/dicom-upload/stowrs-input-element';
import { ViewSendingStatus } from '../../models/view/view-sending-status';
import { SendingStatus } from '../../services/dicom-upload/sending-status';
import { extractorByStudyIds } from '../../utility/extractor';
import { UnsubscribeOnDestroy } from '../../../../utils/UnsubscribeOnDestroy';
import { Patient } from '../../models/internal/Patient';
import { PatientService } from '../../services/patient.service';
import EstensaModel from 'estensa-module';
import { initializeCSTools, initializeImageLoader, readFiles, store as larvitarStore } from 'larvitar';
import { AuditService } from '../../services/audit/audit.service';
import { AuditItemsRequest, AuditRequest, ImportTypeEnum, SourceCallEnum } from '../../services/audit/audit-request';
import { AuditClientService } from '../../services/audit/audit-client.service';
import { SendingApplicationService } from '../../../../sending-application.service';
import { VnaDicomRequest } from '../../models/vna-dicom-web/vna-dicom-request.classes';
import { VnaSetRequestService } from '../../services/vna-dicom-web/vna-set-request.service';
import { Command } from '../../models/vna-dicom-web/enum';
import { MatDialog } from '@angular/material/dialog';
import { ActionDialog } from '../../models/message-dialog/enum';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PatientDetailsFormComponent } from '../../components/patient-details-form/patient-details-form.component';
import { ImportFileComponent } from '../../../import-file/import-file.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { StudyComponent } from '../../components/study/study.component';
import { MessageDialogComponent } from '../../components/message-dialog/message-dialog.component';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'dicom-landing-page',
    templateUrl: './landing-page.component.html',
    styleUrls: ['./landing-page.component.scss'],
    providers: [DatePipe],
    imports: [
        MatProgressSpinner,
        NgIf,
        PatientDetailsFormComponent,
        ImportFileComponent,
        MatCheckbox,
        TranslatePipe,
        NgForOf,
        StudyComponent,
        MessageDialogComponent,
        FormsModule,
        AsyncPipe,
        NgClass
    ],
    standalone: true,
})
export class DicomLandingPage extends SePageComponent implements OnInit, OnChanges {
    @Input() patientDetails?: PatientDetails;
    @Input() originPage: OriginPage;

    _acceptFolder: boolean = true;
    _data: ViewStudy[];
    acceptedTypeFile: string = '*.*';
    auditData = new Map<string, AuditRequest>();
    checkAll: boolean;
    childrenEvent$: Map<string, BehaviorSubject<boolean>>;
    isVNA: boolean = false;
    currentTheme: 'dark' | 'light' = 'light';
    disableUploadButton: boolean = true;
    idUserLogged: string = '';
    indeterminate: boolean;
    messageBoxText: string = '';
    patientFormValid: boolean = true;
    patientInfo: PatientDetails;
    sendingApplicationValue: string;
    sendingInstances: Map<string, SendingStatus[]>;
    showPatientForm: boolean = true;
    showSpinner: boolean = false;
    _showWarning: boolean = false;
    readonly dialog = inject(MatDialog);
    private patients: Patients = new Patients();
    private handleChildrenSubscription: Subscription;
    @UnsubscribeOnDestroy()
    private actionsSubscription!: Subscription;

    constructor(
        public pageService: PageService,
        private routerDataService: RouterDataService,
        public uploaderService: DicomUploadService,
        private patientInfoService: PatientService,
        private auditService: AuditService,
        private auditClientService: AuditClientService,
        private authService: AuthenticationService,
        private sendingApplicationService: SendingApplicationService,
        private logger: Logger,
        private vnaSetRequestService: VnaSetRequestService,
        private translate: TranslateService,
    ) {
        super(pageService);
        this._data = [];
        this.checkAll = false;
        this.indeterminate = false;
        this.childrenEvent$ = new Map();
        this.disableUploadButton = true;
        this.handleChildrenSubscription = new Subscription();
        this.sendingInstances = new Map<string, SendingStatus[]>();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.patientDetails || changes.patientDetails) {
            this.patientFormValid = this.patientDetails.Status === 'VALID' || this.patientDetails.Status === 'DISABLED';
        }
    }

    // #region Page Initialization

    ngOnInit() {
        if (this.originPage === OriginPage.VNA_PATIENT_FOLDER) {
            this.isVNA = true;
            this.callSendingApllication();
        }
        this.initDataForm();
        this.initializeLarvitar();
        this.authService.getUserInfo().then((value) => {
            this.idUserLogged = value.userSessionGuid;
        });
        this.messageWarning();
    }

    onFileUploaded(filesList: FileList) {
        const loadedFiles: File[] = [];
        for (const file of filesList) {
            loadedFiles.push(file);
        }
        this.loadDicomFiles(loadedFiles);
    }

    loadDicomFiles(files: File[]) {
        let startSpinner = this.onLoadingSpinner(files);
        if (startSpinner) {
            this.uploaderService.showSpinner$.next(true);
        }
        const onLoadingPatients: Patients = new Patients();
        readFiles(files).then((seriesStack: any) => {
            for (const serieKey of keys(seriesStack)) {
                const currentSerie: Series = seriesStack[serieKey];
                const instances: { [key: string]: Instance } = this.getInstances(currentSerie, serieKey);
                for (const instanceKey of keys(instances)) {
                    onLoadingPatients.addSeriesElement(new SeriesElement(instances[instanceKey]));
                    this.patients.addSeriesElement(new SeriesElement(instances[instanceKey]));
                }
            }
            let onLoadingViewStudies = onLoadingPatients.generateViewStudies();
            let oldIds: Set<string> = this.retrieveOldIds();
            let merged = mergeStudyArrays({ toMerge: onLoadingViewStudies, existing: this._data });
            let newIds: Set<string> = new Set<string>();
            merged.filter((v) => !oldIds.has(v.data.id)).forEach((v) => newIds.add(v.data.id));
            this.buildChildrenEvent$(newIds);
            this.handleChildrenEvents();
            this._data = merged;
            if (startSpinner) {
                this.uploaderService.showSpinner$.next(false);
            }
        });
    }

    disableImportAfterAllSent() {
        return this._data.every((el) => el.status.sendingStatus === 'SENT');
    }

    toggleAll() {
        const b = !this.checkAll;
        this.childrenEvent$.forEach((s) => s.next(b));
    }

    // #endregion

    // #region load DICOM Files

    onChecked(id: string, value: boolean) {
        this.childrenEvent$.get(id)?.next(value);
    }

    onRemoveStudy(idStudy: string) {
        this._data = this._data.filter((s: ViewStudy) => {
            return s.data.id !== idStudy;
        });
        this.patients.data.forEach((patient: Patient) => {
            if (patient.data.has(idStudy)) {
                patient.data.delete(idStudy);
            }
        });
    }

    onPatientFormChange(patientChanges: PatientDetails) {
        this.patientFormValid = patientChanges.Status === 'VALID' || patientChanges.Status === 'DISABLED';
        this.patientInfo = patientChanges;
    }

    _uploadAll() {
        const someWarning = this._data.some((s: ViewStudy) => s.status.patientNotEqual && s.status.selected);
        if (someWarning) {
            this._showWarning = true;
        } else {
            this.uploadAll();
        }
    }

    _onWarningDialogClick(event: ActionDialog | string) {
        switch (event) {
            case ActionDialog.CONFIRM: {
                this._showWarning = false;
                this.uploadAll();
                break;
            }
            case ActionDialog.CANCEL: {
                this._showWarning = false;
                break;
            }
        }
    }

    // #endregion

    // #region Handle Selected All

    private callSendingApllication() {
        this.sendingApplicationService.getIsses('3').subscribe({
            next: (info: Array<string>) => {
                if (info) {
                    this.sendingApplicationValue = info[0];
                }
            },
            error: (error) => {
                this.logger.error('Errore caricamento isses da sending application');
            },
        });
    }

    private initDataForm(): void {
        if (this.routerDataService) {
            let info =
                this.routerDataService.data?.from === OriginPage.EXTERNAL_DICOM
                    ? this.routerDataService.data.UploadDicomData[0]
                    : this.patientDetails;
            if (!info.Issuer) {
                this.showPatientForm = false;
                this.updateIssuer(info.PatientId);
            }

            if (isNil(this.originPage)) {
                this.originPage = OriginPage.EXTERNAL_DICOM;
            }

            this.patientInfo = {
                Issuer: info.Issuer,
                PatientId: info.PatientId,
                Name: info.Name,
                Surname: info.Surname,
                Sex: info.Sex,
                BirthDate: info.BirthDate,
                AccessionNumber: info.AccessionNumber,
            };
        }
    }

    private async initializeLarvitar() {
        initializeImageLoader();
        initializeCSTools();
        larvitarStore.initialize();
    }

    private updateIssuer(patientId: string) {
        this.showSpinner = true;
        this.patientInfoService.getPatientInfo(patientId).subscribe((patient: EstensaModel.Patient) => {
            this.patientInfo = { ...this.patientInfo, Issuer: patient.issuer };
            this.showSpinner = false;
            this.showPatientForm = true;
            this.patientFormValid = this.patientDetails.Status === 'VALID' || this.patientDetails.Status === 'DISABLED';
        });
    }

    private onLoadingSpinner(files: File[]): boolean {
        if (files.length <= 100) {
            let totalSizeFiles = 0;
            for (const file of files) {
                totalSizeFiles += file.size;
            }
            return totalSizeFiles / 1024 / 1024 > 70;
        }
        return true;
    }

    private getInstances(currentSerie: Series, serieKey: string): { [key: string]: Instance } {
        let instances: { [key: string]: Instance } = currentSerie['instances'];
        if (currentSerie.isMultiframe) {
            instances['dicomFile'] = {
                dataSet: currentSerie.dataSet,
                metadata: currentSerie.metadata,
            };
        }
        return instances;
    }

    private retrieveOldIds(): Set<string> {
        let oldIds: Set<string> = new Set();
        this._data.forEach((v) => oldIds.add(v.data.id));
        return oldIds;
    }

    private buildChildrenEvent$(newIds: Set<string>) {
        if (this.handleChildrenSubscription) {
            this.handleChildrenSubscription.unsubscribe();
        }
        newIds.forEach((id) => this.childrenEvent$.set(id, new BehaviorSubject<boolean>(false)));
    }

    // #endregion

    // #region Upload DICOM File

    private handleChildrenEvents() {
        this.handleChildrenSubscription = combineLatest([...this.childrenEvent$.values()])
            .pipe(
                map((bs): boolean => {
                    const andOr = bs.reduce(
                        (acc: boolean[], cur: boolean): [boolean, boolean] => [acc[0] && cur, acc[1] || cur],
                        [true, false],
                    );
                    this.indeterminate = !andOr[0] && andOr[1];
                    this.disableUploadButton = !(this.indeterminate || andOr[0]);
                    return andOr[0];
                }),
                filter((b: boolean) => this.checkAll !== b),
            )
            .subscribe((check) => this.updateCheckAll(check));
    }

    private updateCheckAll(check: boolean) {
        this.checkAll = check;
    }

    private uploadAll() {
        this.handleAuditData();
        const stowrsElementToSend = this.extractInstancesToSend();
        if (this.isVNA) {
            this.vnaSetRequestService.deleteAllRequestItems();
            this.prepareJsonToRequest(stowrsElementToSend, Command.Start);
            this.callSetRequest();
            this.sendInstancesUploaderVna(stowrsElementToSend);
        } else {
            this.sendInstancesUploader(stowrsElementToSend);
        }
    }

    private sendInstancesUploaderVna(stowrsElementToSend: StowrsInputElement[]) {
        this.uploaderService.sendInstances(stowrsElementToSend, this.patientInfo, this.isVNA).subscribe({
            next: (response) => {
                this.sendingInstances = response;
                this.callAudit(this.sendingInstances);
            },
            error: (er) => {
                this.logger.error('Error on upload');
            },
            complete: () => {
                this.uploaderService.showSpinner$.next(false);
                if (this.isVNA) {
                    this.vnaSetRequestService.buildRequestAfterUpload(this.sendingInstances);
                    this.callSetRequest();
                }
            },
        });
    }

    // #region setRequest VNA

    private sendInstancesUploader(stowrsElementToSend: StowrsInputElement[]) {
        this.uploaderService.sendInstances(stowrsElementToSend, this.patientInfo, this.isVNA).subscribe({
            next: (response) => {
                this.sendingInstances = response;
            },
            error: (er) => {
                this.logger.info('Error on upload');
            },
            complete: () => {
                this.callAudit(this.sendingInstances);
                this.uploaderService.showSpinner$.next(false);
            },
        });
    }

    private prepareJsonToRequest(elementToSend: StowrsInputElement[], command: Command) {
        let studies: Array<RawRequest> = this.vnaSetRequestService.groupByStudy(elementToSend, this.patientInfo);
        studies.forEach((study: RawRequest) => {
            this.vnaSetRequestService.buildRequestInitial(
                study,
                this.patientInfo,
                this.sendingApplicationValue,
                command,
            );
        });
    }

    // #endregion

    private callSetRequest() {
        let requestItems: Map<string, VnaDicomRequest> = new Map<string, VnaDicomRequest>();
        requestItems = this.vnaSetRequestService.getRequestItems();
        requestItems.forEach((val, key) => {
            this.vnaSetRequestService.sendRequest(val).subscribe({
                next: (res) => {
                    this.logger.info(`Send request for study id: ${key}`);
                },
                error: (er) => {
                    this.logger.error(`Error Send request for study id: ${key}`);
                },
                complete: () => {
                    this.logger.info(`Complete send request for study id:  ${key}`);
                    this.vnaSetRequestService.showSpinner$.next(false);
                },
            });
        });
    }

    // #region Audit
    private handleAuditData() {
        let selectedRow: ViewStudy[] = this._data.filter(
            (item: ViewStudy) => item.status.selected && item.status.sendingStatus !== 'SENT',
        );

        let importType: ImportTypeEnum = this.isVNA ? ImportTypeEnum.DicomWithStudy : ImportTypeEnum.Dicom;

        let itemsAudit: AuditItemsRequest = new AuditItemsRequest(
            this.idUserLogged,
            new Date(),
            importType,
            this.origin(this.originPage),
        );
        selectedRow.forEach((row: ViewStudy) => {
            let valueAuditRequest: AuditRequest = this.auditService.convertToAuditRequest(
                row,
                this.patientInfo,
                itemsAudit,
            );
            this.auditService.addAuditData(row.data.id, valueAuditRequest);
        });
        this.auditData = this.auditService.getAuditData();
    }

    private origin(origin: OriginPage): SourceCallEnum {
        switch (origin) {
            case OriginPage.EXTERNAL_DICOM:
                return SourceCallEnum.External;
            case OriginPage.VNA_PATIENT_FOLDER:
                return SourceCallEnum.Vna;
            default:
                return SourceCallEnum.Refertation;
        }
    }

    // #endregion

    private callAudit(response: Map<string, SendingStatus[]>) {
        this.uploaderService.showSpinner$.next(true);
        this.auditData = this.auditService.getAuditData();

        response.forEach((res, key) => {
            let status: SendingStatus[] = response.get(key);
            let valuesRequest: AuditRequest = this.auditData.get(key);

            for (let value of status) {
                if (!value.httpError) {
                    value.instanceFailure == true
                        ? (valuesRequest.errorOnUpload = true)
                        : (valuesRequest.errorOnUpload = false);
                }
            }
            let errorsHttp = status.some((el) => el.httpError);
            if (!errorsHttp) {
                this.auditClientService.auditDicom(valuesRequest).subscribe({
                    error: (er) => {
                        this.auditService.deleteAuditData(key);
                        this.logger.error('Cannot call Broker Audit', er);
                    },
                    complete: () => {
                        this.logger.info('Audit complete');
                    },
                });
            }
        });
        this.auditService.deleteAllData();
        this.auditData = this.auditService.getAuditData();
    }

    private extractInstancesToSend(): Array<StowrsInputElement> {
        const studyIds = this.selectedStudyIds();
        return extractorByStudyIds(this.patients, studyIds);
    }

    private selectedStudyIds() {
        const studyIds: Set<string> = new Set<string>();
        this._data.filter((s) => this.studySelectedAndStillNotSent(s)).forEach((v) => studyIds.add(v.data.id));
        return studyIds;
    }

    // #endregion

    private studySelectedAndStillNotSent(s: ViewStudy): boolean {
        return s.status.selected && s.status.sendingStatus !== ViewSendingStatus.SENT;
    }

    private messageWarning() {
        const confirm = this.translate.instant('ConfirmChoose');
        const importAssociation = this.translate.instant('StudyPatientMatchMessage');
        this.messageBoxText = importAssociation + '. ' + '\n' + confirm;
    }
}
